import { ThumbsDown, ThumbsUp } from 'lucide-react';

import { useCandidateFeedbacksQuery } from '@/modules/candidates/queries/useCandidateFeedbacksQuery';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/modules/ui/components/card/Card';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import type { components } from '@/types/apiSchema';

type FeedbackResponse = components['schemas']['CandidateJobFeedbackResponse'];

interface CandidateFeedbacksListProps {
  jobId: string;
  candidateId: string;
}

function FeedbackItem({ feedback }: { feedback: FeedbackResponse }) {
  const authorName = [feedback.author?.firstName, feedback.author?.lastName]
    .map((v) => v?.trim())
    .filter(Boolean)
    .join(' ');
  const authorLabel = authorName || feedback.author?.email?.trim() || null;

  return (
    <div className="flex items-start gap-3 rounded-lg border p-4">
      <div
        className={
          feedback.isPositive ? 'text-semantic-560' : 'text-semantic-550'
        }
      >
        {feedback.isPositive ? (
          <ThumbsUp className="mt-0.5 size-4" />
        ) : (
          <ThumbsDown className="mt-0.5 size-4" />
        )}
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span
            className={`text-sm font-medium ${feedback.isPositive ? 'text-semantic-560' : 'text-semantic-550'}`}
          >
            {feedback.isPositive ? 'Positive' : 'Negative'}
          </span>
          {authorLabel && (
            <span className="text-muted-foreground text-xs">
              by {authorLabel}
            </span>
          )}
          {feedback.job?.jobTitle && (
            <span className="text-muted-foreground text-xs">
              · {feedback.job.jobTitle}
            </span>
          )}
        </div>
        {feedback.annotation && (
          <div className="mt-1 flex flex-wrap gap-1.5">
            {feedback.annotation
              .split(',')
              .map((r) => r.trim())
              .filter(Boolean)
              .map((reason) => (
                <span
                  key={reason}
                  className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                    feedback.isPositive
                      ? 'border-semantic-560/30 bg-semantic-560/10 text-semantic-560'
                      : 'border-semantic-550/30 bg-semantic-550/10 text-semantic-550'
                  }`}
                >
                  {reason}
                </span>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface FeedbackSectionProps {
  title: string;
  feedbacks: FeedbackResponse[];
}

function FeedbackSection({ title, feedbacks }: FeedbackSectionProps) {
  if (feedbacks.length === 0) return null;

  return (
    <div className="space-y-2">
      <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
        {title}
      </p>
      <div className="space-y-3">
        {feedbacks.map((feedback) => (
          <FeedbackItem key={feedback.id} feedback={feedback} />
        ))}
      </div>
    </div>
  );
}

export function CandidateFeedbacksList({
  jobId,
  candidateId,
}: CandidateFeedbacksListProps) {
  const { data: feedbacks, isLoading } = useCandidateFeedbacksQuery({
    jobId,
    candidateId,
  });

  const hmFeedbacks =
    feedbacks?.filter((f) => f.author?.roles?.includes('HiringManager')) ?? [];
  const recruiterFeedbacks =
    feedbacks?.filter((f) => !f.author?.roles?.includes('HiringManager')) ?? [];

  if (
    !isLoading &&
    hmFeedbacks.length === 0 &&
    recruiterFeedbacks.length === 0
  ) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Feedback</CardTitle>
        <CardDescription>
          Calibration signals from hiring managers and recruiters
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <FeedbackSection title="Hiring Manager" feedbacks={hmFeedbacks} />
            <FeedbackSection title="Recruiter" feedbacks={recruiterFeedbacks} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
